import { useCallback, useEffect } from 'react';
import getLiveUser from '@firsttable/graphql-queries/src/getLiveUser';
import { useLazyQuery } from '@apollo/client';
import { useLocalStorage } from 'usehooks-ts';
import dayjs from 'dayjs';
import { useAuthState } from '../context/AuthContext';

export function useMemberRefresh() {
  const { updateMember, isLoggedIn } = useAuthState();
  const [, setMemberCheckTime] = useLocalStorage<null | number>('firsttable.memberCheckTime', null);
  const [updateLiveMember, { loading, error }] = useLazyQuery(getLiveUser, {
    ssr: false,
    fetchPolicy: 'network-only',
    onCompleted: (d) => {
      updateMember(d.user);
      setMemberCheckTime(dayjs().unix());
    },
  });

  const refreshMember = useCallback(() => {
    if (isLoggedIn) {
      updateLiveMember();
    }
  }, [isLoggedIn, updateLiveMember]);

  return { refreshMember, loading, error };
}

interface UsePeriodicMemberRefreshOptions {
  pollIntervalMinutes?: number;
}

export function usePeriodicMemberRefresh({
  pollIntervalMinutes = 10,
}: UsePeriodicMemberRefreshOptions = {}) {
  const { isLoggedIn } = useAuthState();
  const [memberCheckTime] = useLocalStorage<null | number>('firsttable.memberCheckTime', null);
  const { refreshMember, loading, error } = useMemberRefresh();

  const needsUpdate = useCallback(() => {
    if (!isLoggedIn) return false;
    if (!memberCheckTime) return true;

    const nextUpdateTime = memberCheckTime + pollIntervalMinutes * 60;
    return dayjs().unix() >= nextUpdateTime;
  }, [isLoggedIn, memberCheckTime, pollIntervalMinutes]);

  useEffect(() => {
    if (needsUpdate()) {
      refreshMember();
    }

    const interval = setInterval(
      () => {
        if (needsUpdate()) {
          refreshMember();
        }
      },
      1000 * 60 * pollIntervalMinutes
    );

    return () => {
      clearInterval(interval);
    };
  }, [needsUpdate, refreshMember, pollIntervalMinutes]);

  return {
    needsUpdate: needsUpdate(),
    loading,
    error,
  };
}
